/*!
 * JDMuscle Wishlist (Phase 1, browser-only) - Upload 18, 2026-10-26
 * Adapted from dlerm/shopify-wishlist (https://github.com/dlerm/shopify-wishlist,
 * commit e88dcf5). Original notice:
 *
 * MIT License
 * Copyright (c) 2021 Daniel Lerman
 * Permission is hereby granted, free of charge, to any person obtaining a copy
 * of this software and associated documentation files (the "Software"), to deal
 * in the Software without restriction, including without limitation the rights
 * to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
 * copies of the Software, and to permit persons to whom the Software is
 * furnished to do so, subject to the following conditions:
 * The above copyright notice and this permission notice shall be included in all
 * copies or substantial portions of the Software.
 * THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
 * IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
 * FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
 * AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
 * LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
 * OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE
 * SOFTWARE.
 *
 * JDMuscle changes: JSON storage key jdm_wishlist_v1 ({items:[{h,t}]}, max 100),
 * try/catch storage, one delegated listener (no duplicate bindings), aria-pressed,
 * header count, cross-tab sync, Section Rendering API cards, 404 pruning.
 */
(() => {
  if (window.JdmWishlist) return;
  const KEY = 'jdm_wishlist_v1';
  const MAX = 100;
  const BTN = '[data-jdm-wishlist]';
  let memory = [];

  const valid = (h) => typeof h === 'string' && h.length > 0 && h.length <= 255 && !/[\/?#\s]/.test(h);
  const read = () => {
    try {
      const raw = window.localStorage.getItem(KEY);
      if (!raw) return [];
      const data = JSON.parse(raw);
      const items = Array.isArray(data && data.items) ? data.items : [];
      const seen = new Set();
      return items.filter((i) => i && valid(i.h) && !seen.has(i.h) && seen.add(i.h)).slice(0, MAX);
    } catch (e) {
      return memory.slice();
    }
  };
  const write = (items, detail) => {
    items = items.slice(0, MAX);
    memory = items.slice();
    try { window.localStorage.setItem(KEY, JSON.stringify({ items })); } catch (e) { /* private mode: keep in memory */ }
    sync();
    document.dispatchEvent(new CustomEvent('jdm-wishlist:updated', { detail: Object.assign({ handles: items.map((i) => i.h) }, detail) }));
  };
  const handles = () => read().map((i) => i.h);
  const has = (h) => handles().includes(h);
  const add = (h) => { if (!valid(h) || has(h)) return; write([{ h, t: Date.now() }].concat(read()), { action: 'add', handle: h }); };
  const remove = (h) => { write(read().filter((i) => i.h !== h), { action: 'remove', handle: h }); };
  const toggle = (h) => (has(h) ? remove(h) : add(h));

  let live;
  const announce = (msg) => {
    if (!live) {
      live = document.createElement('div');
      live.className = 'visually-hidden';
      live.setAttribute('aria-live', 'polite');
      live.setAttribute('role', 'status');
      document.body.appendChild(live);
    }
    live.textContent = '';
    setTimeout(() => { live.textContent = msg; }, 30);
  };

  function sync() {
    const set = new Set(handles());
    document.querySelectorAll(BTN).forEach((b) => {
      const on = set.has(b.getAttribute('data-jdm-wishlist'));
      if (b.getAttribute('aria-pressed') !== String(on)) b.setAttribute('aria-pressed', String(on));
      const text = b.querySelector('[data-jdm-wishlist-text]');
      if (text) text.textContent = on ? (b.dataset.textOn || 'Saved to wishlist') : (b.dataset.textOff || 'Add to wishlist');
    });
    document.querySelectorAll('[data-jdm-wishlist-count]').forEach((c) => {
      c.textContent = set.size > 99 ? '99+' : String(set.size);
      c.hidden = set.size === 0;
    });
    document.querySelectorAll('[data-jdm-wishlist-link]').forEach((a) => {
      a.setAttribute('aria-label', set.size ? `Wishlist (${set.size} saved)` : 'Wishlist');
    });
  }

  // One capture-phase listener: the heart never follows the card link or opens quick add.
  document.addEventListener('click', (event) => {
    const btn = event.target.closest && event.target.closest(BTN);
    if (!btn) return;
    event.preventDefault();
    event.stopPropagation();
    const h = btn.getAttribute('data-jdm-wishlist');
    const title = btn.dataset.title || 'Item';
    const wasOn = has(h);
    toggle(h);
    btn.classList.remove('is-popping');
    void btn.offsetWidth;
    if (!wasOn) btn.classList.add('is-popping');
    announce(wasOn ? `${title} removed from wishlist` : `${title} added to wishlist`);
  }, true);

  window.addEventListener('storage', (e) => { if (e.key === KEY) { sync(); renderGrid(); } });

  // Cards added later (filters, infinite scroll, recommendations, quick add) get the right state.
  let queued = false;
  const observer = new MutationObserver((records) => {
    if (queued) return;
    for (const r of records) {
      for (const n of r.addedNodes) {
        if (n.nodeType === 1 && (n.matches(BTN) || n.querySelector(BTN) || n.querySelector('[data-jdm-wishlist-count]'))) {
          queued = true;
          requestAnimationFrame(() => { queued = false; sync(); });
          return;
        }
      }
    }
  });

  /* ---------- Wishlist page ---------- */
  let grid;
  const SECTION = 'jdm-wishlist-card';
  const cache = new Map();
  const fetchCard = (h) => {
    if (cache.has(h)) return Promise.resolve(cache.get(h));
    const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    return fetch(`${root}products/${encodeURIComponent(h)}?section_id=${SECTION}`, { credentials: 'same-origin' })
      .then((res) => {
        if (res.status === 404) return { gone: true };
        if (!res.ok) return { error: true };
        return res.text().then((html) => {
          const doc = new DOMParser().parseFromString(html, 'text/html');
          const card = doc.querySelector('product-card');
          const out = card ? { html: card.outerHTML } : { gone: true };
          cache.set(h, out);
          return out;
        });
      })
      .catch(() => ({ error: true }));
  };
  const pool = async (list, size, fn) => {
    const out = new Array(list.length);
    let i = 0;
    const worker = async () => { while (i < list.length) { const k = i++; out[k] = await fn(list[k]); } };
    await Promise.all(Array.from({ length: Math.min(size, list.length) }, worker));
    return out;
  };
  const setState = (state, count) => {
    if (!grid) return;
    const root = grid.closest('[data-jdm-wishlist-page]');
    root.dataset.state = state;
    const label = root.querySelector('[data-jdm-wishlist-summary]');
    if (label) label.textContent = count ? `${count} saved item${count === 1 ? '' : 's'}` : '';
  };
  let renderToken = 0;
  async function renderGrid() {
    if (!grid) return;
    const token = ++renderToken;
    const list = handles();
    if (!list.length) { grid.innerHTML = ''; setState('empty', 0); return; }
    setState('loading', list.length);
    const results = await pool(list, 4, fetchCard);
    if (token !== renderToken) return;
    const gone = list.filter((h, k) => results[k] && results[k].gone);
    const frag = document.createDocumentFragment();
    list.forEach((h, k) => {
      const r = results[k];
      if (!r || !r.html) return;
      const li = document.createElement('li');
      li.className = 'grid__item jdm-wl-item';
      li.dataset.handle = h;
      li.innerHTML = r.html;
      const rm = document.createElement('button');
      rm.type = 'button';
      rm.className = 'jdm-wl-remove';
      rm.dataset.jdmWishlistRemove = h;
      const t = li.querySelector('[data-jdm-wishlist]');
      rm.textContent = 'Remove';
      rm.setAttribute('aria-label', `Remove ${(t && t.dataset.title) || 'item'} from wishlist`);
      li.appendChild(rm);
      frag.appendChild(li);
    });
    grid.innerHTML = '';
    grid.appendChild(frag);
    if (gone.length) write(read().filter((i) => !gone.includes(i.h)), { action: 'prune', handles: gone });
    const shown = grid.children.length;
    setState(shown ? 'list' : 'empty', shown);
    sync();
  }
  document.addEventListener('click', (event) => {
    const rm = event.target.closest && event.target.closest('[data-jdm-wishlist-remove]');
    if (!rm) return;
    event.preventDefault();
    const h = rm.dataset.jdmWishlistRemove;
    remove(h);
    announce('Removed from wishlist');
  });
  document.addEventListener('jdm-wishlist:updated', (e) => {
    if (!grid || !e.detail || e.detail.action === 'prune') return;
    if (e.detail.action === 'remove') {
      const li = grid.querySelector(`li[data-handle="${CSS.escape(e.detail.handle)}"]`);
      if (li) {
        const next = li.nextElementSibling || li.previousElementSibling;
        li.remove();
        const focusTarget = next && next.querySelector('.jdm-wl-remove');
        if (focusTarget) focusTarget.focus({ preventScroll: true });
      }
      const n = grid.children.length;
      setState(n ? 'list' : 'empty', n);
      if (!n) { const cta = document.querySelector('[data-jdm-wishlist-empty] a'); if (cta) cta.focus({ preventScroll: true }); }
    } else if (e.detail.action === 'add') {
      renderGrid();
    }
  });

  const init = () => {
    sync();
    observer.observe(document.body, { childList: true, subtree: true });
    grid = document.querySelector('[data-jdm-wishlist-grid]');
    if (grid) renderGrid();
  };
  window.JdmWishlist = { handles, has, add, remove, toggle, sync, key: KEY };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init, { once: true });
  else init();
})();
